import { buildCorrectSequence, isSequenceReviewQuestion } from './sequenceQuestionScoring';

export type WrongReviewSource = { userId: string; key: string; testIds: string[] };
type WrongItem = { id: string; testId: string; bankVersion: string; systemType: string; level: number; reviewVersionKey?: string };
type Progress = Record<string, string[]>;
const storageKey = (userId: string) => `wrong-review-resolved:v1:${userId}`;
export function wrongQuestionKey(item: WrongItem) {
  const bank = item.bankVersion === '3.0' && item.systemType === 'workbook' ? 'workbook'
    : item.bankVersion === 'CSCA' && item.systemType.startsWith('mooc_') ? 'mooc' : 'main';
  return JSON.stringify([item.bankVersion, bank, item.level, item.id, ...(item.bankVersion === 'CSCA' && item.reviewVersionKey ? [item.reviewVersionKey] : [])]);
}
export function readWrongProgress(userId: string): Progress {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey(userId)) || '{}');
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(Object.entries(value).filter(([, ids]) => Array.isArray(ids) && ids.every(id => typeof id === 'string'))) as Progress;
  } catch { return {}; }
}
export const isWrongResolved = (item: WrongItem, progress: Progress) => progress[wrongQuestionKey(item)]?.includes(item.testId) || false;
export function wrongReviewSource(userId: string, item: WrongItem, all: WrongItem[]): WrongReviewSource {
  const key = wrongQuestionKey(item);
  return { userId, key, testIds: [...new Set(all.filter(other => wrongQuestionKey(other) === key).map(other => other.testId))] };
}
export function saveCorrectReviews(userId: string, sources: Array<WrongReviewSource | undefined>) {
  const progress = readWrongProgress(userId);
  for (const source of sources) {
    if (!userId || !source || source.userId !== userId) continue;
    progress[source.key] = [...new Set([...(progress[source.key] || []), ...source.testIds])];
  }
  try { localStorage.setItem(storageKey(userId), JSON.stringify(progress)); return { progress, persisted: true }; }
  catch { return { progress, persisted: false }; }
}
// Only a confirmed objective answer removes a mistake. Unanswered and ungraded
// writing answers must remain in the list.
export function isCorrectWrongReview(question: any, answer: any): boolean {
  if (question.gradingPending || question.grading_pending) return false;
  if (isSequenceReviewQuestion(question)) {
    if (!Array.isArray(answer?.sequence)) return false;
    const expected = buildCorrectSequence(question);
    return expected.length > 0 && expected.length === answer.sequence.length && expected.every((word, index) => word === answer.sequence[index]);
  }
  return Number.isInteger(answer) && answer >= 0 && Number.isInteger(question.correctAnswer) && question.correctAnswer >= 0 && (question.correctAnswers || [question.correctAnswer]).includes(answer);
}
